import { FreepikSource } from './freepik-source';
import type {
  ImageSearchOptions,
  ImageSearchResult,
  ImageSource,
} from './types';
import { UndrawSource } from './undraw-source';
import { UnsplashSource } from './unsplash-source';

// Configuration for source priority - easily adjustable
export const IMAGE_SOURCE_CONFIG = {
  sources: [
    { name: 'undraw', priority: 1 },
    { name: 'freepik', priority: 2 },
    { name: 'unsplash', priority: 3 },
  ]
};

export class ImageSourceManager {
  private readonly sources: Map<string, ImageSource> = new Map();
  private readonly sourceOrder: string[] = [];
  private static instance: ImageSourceManager;
  private readonly resultCache = new Map<string, { results: ImageSearchResult[]; timestamp: number; source: string }>();
  private readonly CACHE_DURATION = 300000; // 5 minutes

  private constructor() {
    // Initialize sources
    const availableSources = {
      undraw: new UndrawSource(),
      freepik: new FreepikSource(),
      unsplash: new UnsplashSource(),
    };

    // Set up sources based on config
    IMAGE_SOURCE_CONFIG.sources
      .sort((a, b) => a.priority - b.priority)
      .forEach(config => {
        const source = availableSources[config.name as keyof typeof availableSources];
        if (source) {
          this.sources.set(config.name, source);
          this.sourceOrder.push(config.name);
        }
      });
  }

  static getInstance(): ImageSourceManager {
    if (!ImageSourceManager.instance) {
      ImageSourceManager.instance = new ImageSourceManager();
    }
    return ImageSourceManager.instance;
  }

  async searchImages(options: ImageSearchOptions): Promise<ImageSearchResult[]> {
    // Check combined cache first
    const cacheKey = JSON.stringify(options);
    const cached = this.resultCache.get(cacheKey);
    if (cached && (Date.now() - cached.timestamp) < this.CACHE_DURATION) {
      return cached.results;
    }

    // Query sources strictly by configured priority; let backend handle availability
    for (const sourceName of this.sourceOrder) {
      const source = this.sources.get(sourceName);
      if (!source) continue;

      try {
        // Let each source manage its own timeout; don't override here
        const fromSource = await source.search(options);

        if (Array.isArray(fromSource) && fromSource.length > 0) {
          this.resultCache.set(cacheKey, {
            results: fromSource,
            timestamp: Date.now(),
            source: sourceName,
          });
          return fromSource;
        }
      } catch (error) {
        console.error(`Error searching in ${sourceName}:`, error);
        // Continue to next source
      }
    }

    console.warn(`No images found from any source for query: ${options.query}`);
    return [];
  }

  async searchVectors(options: ImageSearchOptions): Promise<ImageSearchResult[]> {
    return this.searchImages(options);
  }
}
